import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { DPC_PRICE_PER_MONTH } from '../config/pricing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

export default function DPCRegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '',
    practice_name: '', npi_number: '', city: '', state: '', phone: '',
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/dpc/register`, form);
      localStorage.setItem('dpc_token', res.data.token);
      localStorage.setItem('dpc_user', JSON.stringify({
        first_name: res.data.user.first_name,
        last_name: res.data.user.last_name,
        practice_name: res.data.user.practice_name,
        email: res.data.user.email,
        subscription_status: res.data.user.subscription_status,
        trial_ends_at: res.data.user.trial_ends_at,
      }));
      navigate('/dpc/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors';
  const labelClass = 'block text-sm font-medium text-stone-700 mb-1.5';

  return (
    <div className="min-h-screen bg-[#F0EEE9] flex">

      {/* Left panel */}
      <div className="hidden lg:flex lg:w-2/5 bg-[#0D3D4A] flex-col justify-between p-16">
        <Link to="/for-physicians" className="flex items-center gap-2">
          <span className="text-white font-semibold text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            Meridian for Physicians
          </span>
        </Link>
        <div>
          <h2
            className="text-3xl font-medium text-white mb-6 leading-snug"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Your 14-day free trial starts today.
          </h2>
          <p className="text-white/60 text-sm mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Search 600,000+ price records, compare facilities, and refer your cash-pay patients
            to the best option — every time.
          </p>
          <ul className="space-y-3">
            {[
              'No credit card required',
              'Full access during trial',
              `${DPC_PRICE_PER_MONTH} after — cancel any time`,
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-white/40 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-white/30 text-xs">
          Meridian does not provide medical advice. For informational use only.
        </p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-start justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-lg py-8">
          <div className="mb-8">
            <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-2">Physician Registration</p>
            <h1
              className="text-3xl font-medium text-stone-900 mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Create your account
            </h1>
            <p className="text-stone-500 text-sm">Start your 14-day free trial. No credit card required.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="dpc-register-form">

            {/* Name row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>First Name</label>
                <input type="text" value={form.first_name} onChange={set('first_name')}
                  placeholder="Jane" className={inputClass} required data-testid="dpc-first-name" />
              </div>
              <div>
                <label className={labelClass}>Last Name</label>
                <input type="text" value={form.last_name} onChange={set('last_name')}
                  placeholder="Smith" className={inputClass} required data-testid="dpc-last-name" />
              </div>
            </div>

            <div>
              <label className={labelClass}>Email Address</label>
              <input type="email" value={form.email} onChange={set('email')}
                placeholder="you@practice.com" className={inputClass} required data-testid="dpc-email" />
            </div>

            <div>
              <label className={labelClass}>Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={set('password')}
                  placeholder="At least 8 characters"
                  className={`${inputClass} pr-12`}
                  required
                  minLength={8}
                  data-testid="dpc-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8B4B8]"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className={labelClass}>Practice Name</label>
              <input type="text" value={form.practice_name} onChange={set('practice_name')}
                placeholder="Smith DPC" className={inputClass} required data-testid="dpc-practice-name" />
            </div>

            <div>
              <label className={labelClass}>
                NPI Number{' '}
                <span className="text-[#A8B4B8] font-normal">(optional)</span>
              </label>
              <input type="text" value={form.npi_number} onChange={set('npi_number')}
                placeholder="National Provider Identifier" className={inputClass} data-testid="dpc-npi" />
            </div>

            {/* City / State row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>City</label>
                <input type="text" value={form.city} onChange={set('city')}
                  placeholder="Austin" className={inputClass} required data-testid="dpc-city" />
              </div>
              <div>
                <label className={labelClass}>State</label>
                <select value={form.state} onChange={set('state')}
                  className={inputClass} required data-testid="dpc-state">
                  <option value="">Select…</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>Phone Number</label>
              <input type="tel" value={form.phone} onChange={set('phone')}
                placeholder="(512) 555-0100" className={inputClass} required data-testid="dpc-phone" />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="dpc-register-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0D3D4A] text-white py-4 text-sm font-semibold hover:bg-[#0E4557] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="dpc-register-submit"
            >
              {loading ? 'Creating account…' : 'Start Free Trial'}
              {!loading && <ArrowRight size={16} />}
            </button>
          </form>

          <p className="mt-6 text-sm text-stone-500 text-center">
            Already have an account?{' '}
            <Link to="/dpc/login" className="text-[#0D3D4A] font-medium hover:underline">
              Sign in →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
